import { useCallback, useState } from 'react';

import { Box, Stack, Typography } from '@/shared/ui';
import { useAppSelector } from '@/store/hooks';

import {
  METADATA_COMPANY_NAME_FIELD,
  METADATA_FIRST_NAME_FIELD,
  METADATA_LAST_NAME_FIELD
} from '@/config/constants';
import { useUserConfig } from '@/features/_shared/user';
import Preferences from '@/features/profile/Preferences';
import { retrieveFromUserMetadata } from '@/services/storage/userMetadata';
import useDocumentTitleRename from '@/shared/hooks/useDocumentTitleRename';
import { PageInformationContext } from '@/shared/lib/PageInformationContext';
import Loader from '@/shared/ui/feedback/Spinner';
import Tabs from '@/shared/ui/primitives/Tabs';

import AccountDetails from './AccountDetails';
import PersonalDetails from './PersonalDetails';

const settingTabs = [
  { name: 'preferences', label: 'Preferences' },
  { name: 'personal-details', label: 'Personal Details' },
  { name: 'account-details', label: 'Account Details' }
];
const pageInformation = { mainPageTitle: 'Settings' };

const headerSx = {
  display: 'flex',
  width: '100%',
  mt: 3,
  alignItems: 'center',
  justifyContent: 'space-between'
};
const tabRowSx = { display: 'flex', alignItems: 'center' };
const titleSx = { mr: 4 };
const contentStackSx = { width: '100%', mt: 3, flexWrap: 'wrap' };

const Profile = () => {
  const { isLoading: isUserConfigLoading } = useUserConfig();
  const tripTokenState = useAppSelector(state => state.tripToken);

  const [activeTab, setActiveTab] = useState(0);

  useDocumentTitleRename('GX Go | Settings');
  const renderTabContent = useCallback(() => {
    switch (activeTab) {
      case 1:
        return (
          <PersonalDetails
            companyName={retrieveFromUserMetadata(
              tripTokenState.metadata ?? [],
              METADATA_COMPANY_NAME_FIELD
            )}
            email={tripTokenState.userEmail}
            firstName={retrieveFromUserMetadata(
              tripTokenState.metadata ?? [],
              METADATA_FIRST_NAME_FIELD
            )}
            lastName={retrieveFromUserMetadata(
              tripTokenState.metadata ?? [],
              METADATA_LAST_NAME_FIELD
            )}
          />
        );
      case 2:
        return <AccountDetails />;
      default:
        return <Preferences />;
    }
  }, [activeTab, tripTokenState.metadata, tripTokenState.userEmail]);

  return (
    <>
      <Box sx={headerSx}>
        <Box sx={tabRowSx}>
          <Typography
            component='h1'
            sx={titleSx}
            variant='h2'
          >
            Settings
          </Typography>
          <PageInformationContext value={pageInformation}>
            <Tabs
              activeTab={activeTab}
              minTabWidth={120}
              tabs={settingTabs}
              onChange={index => {
                setActiveTab(index);
              }}
            />
          </PageInformationContext>
        </Box>
      </Box>

      {isUserConfigLoading ? (
        <Loader />
      ) : (
        <Stack
          useFlexGap
          direction='row'
          spacing={{ xs: 2, sm: 3 }}
          sx={contentStackSx}
        >
          {renderTabContent()}
        </Stack>
      )}
    </>
  );
};

export default Profile;
